import { WaterFactoryModelFactory } from "@/core/models/water-factory.model";
import { WaterInvoiceModel } from "@/core/models/water-invoice.model";
import { formatCurrencyPipe } from "@/core/pipes/format-currency.pipe";
import { formatDatePipe } from "@/core/pipes/format-date.pipe";
import { WaterFactoryRepo } from "@/core/repos/tables/water-factory.repo";
import { CreateInvoicePayload, WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import container from "@/lib/di/container";
import { QRCode, Button, Modal, Form, message, Input, DatePicker, InputNumber, Select, ConfigProvider, Empty, Popconfirm, Tooltip } from "antd";
import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { InvoiceTable } from "./InvoiceModal";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { data } from "react-router";
import { Dayjs } from "dayjs";
import { ContractModel } from "@/core/models/contracts/contract.model";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { useDebounce } from "use-debounce";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { CloseOutlined, ExclamationCircleFilled, EyeOutlined } from "@ant-design/icons";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { WaterIndexRepo } from "@/core/repos/tables/water-index.repo";
import { WaterIndexModel } from "@/core/models/water-index.model";
import { AppUserModel } from "@/core/models/app-user.model";
import { useForm } from "antd/es/form/Form";
import { DEF_DATE_FORMAT, DEF_MONTH_FORMAT } from "@/app/home/utils/global.utils";

const waterInvoiceRepo = container.get(WaterInvoiceRepo);
const contractRepo = container.get(ContractRepo);
const waterIndexRepo = container.get(WaterIndexRepo);

interface CreateInvoiceModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
}
export const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({isModalOpen, setIsModalOpen}) => {
    const [previewLoading, setPreviewLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<WaterInvoiceModel>();
    const [invoicePayload, setInvoicePayload] = useState<CreateInvoicePayload>();
    const [month, setMonth] = useState<string>();
    const [year, setYear] = useState<string>();
    const [waterUserId, setWaterUserId] = useState<string>();
    const [isManual, setManual] = useState<boolean>(false);
    const [form] = useForm();
    
    const [appUsers, setAppUsers] = useState<AppUserModel[]>([]);
    
    const [userOptions, setUserOptions] = useState<ContractModel[]>([]);
    const [userSearchQuery, setUserSearchQuery] = useState<string>("");
    const [debouncedUserSearchQuery] = useDebounce(userSearchQuery, 200);

    const fetchUsers = async (search?: string) => {
        const users = await contractRepo.getData(1, {search}, 20);
        if (users) {
            setUserOptions(users.data);
        }
    }

    // Initialize select options data
    const init = async () => {
        await fetchUsers();
    }

    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    useEffect(() => {
        fetchUsers(debouncedUserSearchQuery);
    }, [debouncedUserSearchQuery]);

    const parseDateToMonthYear = (date: Dayjs) => {
        const month = (date.month() + 1).toString();
        const year = date.year().toString();
        return {month, year};
    }

    const handlePreview = async () => {
        try {
            if (!waterUserId||!month||!year) return;
            setPreviewLoading(true);
            const waterUser = await contractRepo.detail(waterUserId);
            if (!waterUser) {
                setPreviewLoading(false);
                message.error({
                    content: "Không tìm thấy thông tin khách hàng!"
                });
                return;
            }
            if (isManual) {
                const formData = await form.validateFields();
                const payload: CreateInvoicePayload = {
                    ...formData,
                    month,
                    year,
                    waterFactoryId: waterUser.waterFactoryId,
                    waterUserId: waterUser.id
                }
                const result = await waterInvoiceRepo.previewInvoice(payload);
                setPreviewLoading(false);
                if (result) {
                    if (typeof result == "string") {
                        message.error({
                            content: result
                        })
                    } else {
                        setData(result);
                        setInvoicePayload(payload);
                    }
                } else {
                    message.error({
                        content: "Xem trước hoá đơn thất bại!"
                    })
                }
            } else {
                const waterIndexs = await waterIndexRepo.allByWaterUser(waterUser.id);
                const waterIndex = waterIndexs.find(i => i.month.toString()==month&&i.year.toString()==year);
                if (!waterIndex||!waterIndex.useAmount||!waterIndex.oldNumber) {
                    setPreviewLoading(false);
                    message.error({
                        content: "Không tìm thấy thông tin chỉ số nước tháng này!"
                    });
                    return;
                }
                const payload: CreateInvoicePayload = {
                    month,
                    year,
                    readDate: waterIndex.createdAt,
                    readDateLast: waterUser.lastReadDate,
                    readerUserName: waterIndex.createdBy,
                    waterFactoryId: waterIndex.waterFactoryId,
                    waterMeterNumber: waterIndex.useAmount,
                    waterMeterNumberLast: waterIndex.oldNumber,
                    waterUserId: waterUser.id
                }
                const result = await waterInvoiceRepo.previewInvoice(payload);
                setPreviewLoading(false);
                if (result) {
                    if (typeof result == "string") {
                        message.error({
                            content: result
                        })
                    } else {
                        setData(result);
                        setInvoicePayload({
                            month,
                            year,
                            waterUserId: waterUser.id,
                            waterIndexId: waterIndex.id
                        });
                    }
                } else {
                    message.error({
                        content: "Xem trước hoá đơn thất bại!"
                    })
                }
            }      
        } catch (error) {
            console.log(error);
        }
    };

    const handleSave = async () => {
        try {
            if (!invoicePayload) return;
            setLoading(true);
            let result;
            if (isManual) {
                result = await waterInvoiceRepo.addInvoiceManual(invoicePayload);
            } else {
                result = await waterInvoiceRepo.addInvoice(invoicePayload);
            }
            setLoading(false);
            if (result == true) {
                setIsModalOpen(false);
                message.success({
                    content: "Tạo hoá đơn thành công!"
                })
            } else if (typeof result == "string") {
                message.error({
                    content: result
                })
            } else {
                message.error({
                    content: "Tạo hoá đơn thất bại!"
                })
            }
            
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    const resetPreview = () => {
        setData(undefined);
        setInvoicePayload(undefined);
    }

    const handleCancel = () => {
        setIsModalOpen(false);
    };
    return <Modal open={isModalOpen} onCancel={handleCancel} width={1200} 
        style={{
            top: 20
        }}
        styles={{
            mask: {
                backdropFilter: 'blur(2px)',
                backgroundColor: 'var(--primary-mask)'
            }
        }}
        footer={[
            <div key={1} className="w-full flex flex-row justify-end px-1.5">
                <Popconfirm
                    title={`Lưu thay đổi?`}
                    description={`Bạn có chắc chắn muốn lưu thay đổi?`}
                    onConfirm={handleSave}
                    okText="Lưu thay đổi"
                    cancelText="Hủy"
                    okButtonProps={{style: {backgroundColor: "var(--primary-color)"}}}
                    icon={<ExclamationCircleFilled style={{color: "var(--danger-color)"}} />}
                >
                    <Tooltip
                        title={(!data||!invoicePayload)?"Bạn cần xem trước trước khi tạo hoá đơn!":null}
                    >
                        <Button key="submit" type="primary" loading={loading}
                            disabled={!data||!invoicePayload}
                            style={{ 
                                background: (!data||!invoicePayload)?"var(--disabled-button)":"var(--primary-color)", 
                                paddingRight: 48, 
                                paddingLeft: 48, 
                                fontSize: 18,
                                fontWeight: 600,
                                paddingTop: 18, 
                                paddingBottom: 18,
                                }}
                        >
                            LƯU
                        </Button>
                    </Tooltip>
                </Popconfirm>
            </div>,
        ]}
    >
        <div className="flex flex-row w-full items-start justify-start select-none">
            <p className="ml-4 text-2xl font-bold text-primary">TẠO HOÁ ĐƠN MỚI</p>
        </div>
        <p className="text-xl font-bold text-primary ml-4 mt-6">1. THÔNG TIN KHÁCH HÀNG</p>
        <ConfigProvider theme={MODAL_THEME}>
            <Form<Partial<CreateInvoicePayload>>
                name="basic"
                className="grid grid-cols-3 gap-2 px-4 pb-2"
                form={form}
                layout="vertical"
                disabled={data!=undefined&&invoicePayload!=undefined}
            >
                <Form.Item
                    className="w-full col-span-2"
                    label="Hộ dùng nước"
                >
                    <Select
                        showSearch
                        onChange={(value) => setWaterUserId(value)}
                        onSearch={(query) => setUserSearchQuery(query)}
                        notFoundContent={null}
                        defaultActiveFirstOption={false}
                        placeholder="Nhập Mã KH, Tên KH, ..."
                        filterOption={false}
                        options={userOptions.map(user => ({
                            label: `${user.code} - ${user.name} - ${user.userName}`,
                            value: user.id
                        }))}
                    />
                </Form.Item>
                <Form.Item
                    label="Tháng"
                    getValueProps={(i) => ({ value: dayjsPipe(i) })}
                >
                    <DatePicker.MonthPicker
                        format={DEF_MONTH_FORMAT}
                        className="w-full"
                        onChange={(date) => {
                            const {month, year} = parseDateToMonthYear(date);
                            setMonth(month);
                            setYear(year);
                        }}
                    />
                </Form.Item>
                <Form.Item
                    className="w-full"
                    label="Kiểu tạo hoá đơn"
                >
                    <Select onChange={(value) => setManual(value)} value={isManual}>
                        <Select.Option value={false}>Tự động</Select.Option>
                        <Select.Option value={true}>Thủ công</Select.Option>
                    </Select>   
                </Form.Item>
                {isManual&&<>
                    <Form.Item<Partial<CreateInvoicePayload>>
                        label="Chỉ số đầu"
                        name="waterMeterNumberLast"
                        rules={[{ required: true, message: 'Bạn phải nhập chỉ số đầu!' }]}
                    >
                        <InputNumber className="w-full" placeholder="Nhập tên"></InputNumber>
                    </Form.Item>
                    <Form.Item<Partial<CreateInvoicePayload>>
                        label="Chỉ số cuối"
                        name="waterMeterNumber"
                        rules={[{ required: true, message: 'Bạn phải nhập chỉ số cuối!' }]}
                    >
                        <InputNumber className="w-full" placeholder="Nhập tên"></InputNumber>
                    </Form.Item>
                    <Form.Item<Partial<CreateInvoicePayload>>
                        label="Ngày đọc chỉ số đầu"
                        name="readDateLast"
                        getValueProps={(i) => ({ value: dayjsPipe(i) })}
                    >
                        <DatePicker format={DEF_DATE_FORMAT} className="w-full" placeholder="Chọn ngày đọc chỉ số đầu"></DatePicker>
                    </Form.Item>
                    <Form.Item<Partial<CreateInvoicePayload>>
                        label="Ngày đọc chỉ số cuối"
                        name="readDate"
                        getValueProps={(i) => ({ value: dayjsPipe(i) })}
                    >
                        <DatePicker format={DEF_DATE_FORMAT} className="w-full" placeholder="Chọn ngày đọc chỉ số đầu"></DatePicker>
                    </Form.Item>
                    <Form.Item<Partial<CreateInvoicePayload>>
                        label="Người đọc số nước"
                        name="readerUserName"
                    >
                        <Select className="w-full" placeholder="Chọn người đọc số nước">
                            {appUsers.map(user => <Select.Option key={user.userName} value={user.userName}>{user.userName} - {user.fullName}</Select.Option>)}
                        </Select>
                    </Form.Item>
                </>}
            </Form>
        </ConfigProvider>
        <div className="mb-3 mx-4 border-t border-dashed border-gray-300"></div>
        <div className="flex flex-row justify-between items-center mr-3">
            <p className="text-xl font-bold text-primary ml-4">2. XEM TRƯỚC HOÁ ĐƠN</p>
            {data&&invoicePayload&&<Button type="default" icon={<CloseOutlined />} onClick={resetPreview}>Huỷ xem trước</Button>}
        </div>
        <div className="flex flex-col items-center">
            <div className="py-4 px-4 w-full">
                {data
                ?<InvoiceTable data={data} preview></InvoiceTable>
                :<div className="h-[150px] pb-[32px] flex flex-col items-center justify-center">
                    <ConfigProvider theme={MODAL_THEME}>
                        <Tooltip
                            title={(!waterUserId||!month||!year)?"Bạn cần chọn Khách hàng và Tháng tạo hoá đơn!":null}
                        >
                            <Button
                                icon={<EyeOutlined />}
                                onClick={handlePreview}
                                disabled={!waterUserId||!month||!year}
                                type='primary'
                                style={{
                                  background: (!waterUserId||!month||!year)?"var(--disabled-button)":"var(--primary-color)",
                                }}
                            >Xem trước</Button>
                        </Tooltip>
                    </ConfigProvider>
                </div>}
            </div>
        </div>
        <div className="mb-5 mx-4 border-t border-dashed border-gray-300"></div>
    </Modal>
} 